import Handlebars from "utilities/vendor/handlebars/4.7.7/js/handlebars";
import hbs from "utilities/vendor/handlebars/4.7.7/js/handlebars";
/* global Handlebars spaParams */

/* Button Helper */
const UTILITY_NAME = 'helpers/helpers-button';
const VALID_BREAKPOINT_KEYS = [
  'small-up',
  'medium-up',
  'large-up',
  'small-only',
  'medium-only',
  'large-only',
];
const VALID_TAGS = ['button', 'a'];
const DEFAULT_TAG = 'button';
const DEFAULT_BASE_CLASS = 'spa-btn';
const VALID_TYPE_ATTRIBUTES = ['button', 'submit', 'reset'];
const REQUIRED_BUTTON_ATTRIBUTE_KEYS = ['type'];
const ACTION_MODIFIERS = {
  layer: 'spa-ui-layer-link spa-fn',
  popup: 'spa-fn spa-boa-window'
};
const ACTION_LAYER_MODIFIERS = {
  interstitialType: 'spa-ui-layer-interstitial',
  inputHelpLinkType: 'toggle-trigger-link',
  inputHelpIconType: 'toggle-trigger svg-button',
  layerType: 'spa-ui-layer-tooltip-link',
};

const helpers = {
  // generates a button from a jcr config
  button(config, secureHost) {
    if (!config) {
      const msg = `Error in ${UTILITY_NAME}: 'button' helper method requires a config. ` +
        `If you are using multiple languages ensure all language-based JCRs have the same named config property.`;
      throw new Error(msg);
    }

    // determine the base class
    // note: it's important to allow an empty jcr value "" to be considered a valid base value
    //  in order to override the default 'spa-btn' base class with NO base class (mainly for links)
    const baseExists = Object.prototype.hasOwnProperty.call(config, 'base');
    const baseIsValid = (baseExists && (config.base === '' || config.base.length));
    const BASE_CLASS = (baseIsValid) ? config.base : DEFAULT_BASE_CLASS;

    const BTN_PREFIX = `${BASE_CLASS}--`;
    const ADDITIONAL_CLASSES = config.classes || '';

    let modifierClasses = '';
    let attributes = '';
    let content = config.content || '';

    // determine element tag (or default to 'button' tag)
    const TAG = (VALID_TAGS.indexOf(config.tag) !== -1) ? config.tag : DEFAULT_TAG;
    const isButton = TAG === 'button';

    const EXISTING_ATTRIBUTE_KEYS = (config.attributes) ? Object.keys(config.attributes) : [];
    const REQUIRED_ATTRIBUTE_KEYS = (isButton) ? REQUIRED_BUTTON_ATTRIBUTE_KEYS : [];

    // convert keywords to class names
    // (for example: the keyword 'primary' will convert to the full 'spa-btn--primary' class name)
    if (config.modifiers) {
      config.modifiers.split(' ').forEach((keyword) => {
        modifierClasses += `${BTN_PREFIX}${keyword} `;
      });
    }

    // convert responsive keywords to class names
    if (config.breakpoints) {
      // check only valid breakpoint properties
      VALID_BREAKPOINT_KEYS.forEach((breakpoint) => {
        const keywords = config.breakpoints[breakpoint];
        // convert keywords to full modifier classnames
        if (keywords) {
          keywords.split(' ').forEach((keyword) => {
            modifierClasses += `${BTN_PREFIX}${keyword}-at-${breakpoint} `;
          });
        }
      });
    }

    // actionType
    // (for triggering global ui-layers stuff: modals, interstitials, popups, etc)
    if (config.actionType) {
      const type = config.actionType.type;
      const options = config.actionType.options;

      // add tracking attribute
      attributes += 'data-bactmtrack ';

      if (options) {
        // add stringified, escaped options as an attribute
        let stringifiedOptions = JSON.stringify(config.actionType.options);
        let escapedOptions = Handlebars.Utils.escapeExpression(stringifiedOptions);
        attributes += `data-options="${escapedOptions}" `;
      }

      // function map to handle the main action types (layer | popup)
      // note: type 'popup' has no specific handling requirements anymore
      const typeSwitch = {
        layer() {
          // add ada text to button content
          // TODO: A JIRA ticket has been created for future work to enable translation of the word "layer".
          // http://code.bankofamerica.com/issues2/browse/SPARTA-2593
          content += `<span class="spa-ada-text ada-hidden">&nbsp;layer</span>`;

          if (options) {
            if (options.rel) {
              attributes += `rel="${options.rel}" `;
            }

            // add 'layer' subtype-based modifiers
            Object.keys(ACTION_LAYER_MODIFIERS).some((key) => {
              if (options.hasOwnProperty(key)) {
                modifierClasses += ACTION_LAYER_MODIFIERS[key] + ' ';
                return true;
              }
              return false;
            });
          }
        },
      };

      if (type) {
        // add type-based modifiers
        if (ACTION_MODIFIERS.hasOwnProperty(type)) {
          modifierClasses += ACTION_MODIFIERS[type] + ' ';
        }
        // execute the function map
        if (typeSwitch[type]) {
          typeSwitch[type]();
        }
      }
    } else if (config.adaText) {
      // adding this in as else to the actionType since it has it's own hidden ada text
      content += `<span class="spa-ada-text ada-hidden">&nbsp;${config.adaText}</span>`;
    }

    // add required attribute keys to key list (if missing)
    REQUIRED_ATTRIBUTE_KEYS.forEach((attr) => {
      if (EXISTING_ATTRIBUTE_KEYS.indexOf(attr) === -1) {
        EXISTING_ATTRIBUTE_KEYS.push(attr);
      }
    });

    // build the 'attribute' string
    EXISTING_ATTRIBUTE_KEYS.forEach((attr) => {
      let val = (config.attributes && config.attributes[attr]) ? config.attributes[attr] : undefined;

      if (isButton) {
        if (attr === 'type') {
          // ensure 'type' attribute gets set to an appropriate value (for ada compliance)
          if (VALID_TYPE_ATTRIBUTES.indexOf(val) === -1) {
            val = 'button';
          }
        }
      }

      // if the button link is 'secure', use the secureHost parameter to transform the 'href' attribute
      if (Object.prototype.hasOwnProperty.call(config, 'secure') && config.secure === true) {
        if (!config.attributes || !config.attributes.href) {
          throw new Error(`Error in ${UTILITY_NAME}: 'config.attributes' is missing an 'href' property.`);
        } else if (attr === 'href') {
          if (!/^http.+/i.test(val)) {
            if ((!secureHost || typeof secureHost !== 'string')) {
              if (typeof spaParams !== 'undefined' && spaParams.siteParams) {
                secureHost = spaParams.siteParams.secureHost;
              } else {
                throw new Error(`Error in ${UTILITY_NAME}: secureHost is not defined and is necessary for secure link button.`);
              }
            }

            val = secureHost + val;
          }
        }
      }

      // append this attribute to the 'attributes' string
      attributes += `${attr}="${val}" `;
    });

    // finalize the 'class' attribute
    const CLASSES = `class="${BASE_CLASS} ${modifierClasses} ${ADDITIONAL_CLASSES}"`;

    // finalize attributes string
    attributes = `${CLASSES} ${attributes}`;

    // adaText

    // finalize output
    const output = `<${TAG} ${config.vueOptionsStr || ''} ${attributes}>${content}</${TAG}>`;

    // output
    return new Handlebars.SafeString(output);
  },
};

/**
 * @class ButtonHelper
 * @description Class used to register our helper with Handlebars.
 */
class ButtonHelper {
  /**
   * @method register
   * @param {Object} Handlebars Reference to Handlebars for helper registration.
   * @description Registers helper with Handlebars.
   */
  static register(Handlebars) {
    Object.keys(helpers).forEach((key) => {
      Handlebars.registerHelper(key, helpers[key]);
    });
  }
}

module.exports = ButtonHelper;
